import {
	TeamDataSchema,
	type ClientInit,
	type FileUpload,
	type RoundState,
	type TaskState,
	type TaskStateLocal,
	type TaskStatus,
	type TeamRecord,
} from 'shared/modelTypes/client';
import type { Game, GameRound, GameTask } from 'shared/modelTypes/game';
import type { GameSkin } from 'shared/modelTypes/skin';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { emitMessage, updateGpsRequired, uploadTaskSubmission } from './api';
import { haversineDistance } from 'shared/util';
import { haptics } from './lib';
import type { EventStatus } from 'shared/modelTypes/event';
import type { MessageAttachmentsData } from 'shared/modelTypes/media';
import { appConfig } from './appConfig';

export type ChatMessage = {
	id: string;
	teamId?: string | null;
	message: string;
	attachments?: MessageAttachmentsData;
	channel: ChatChannel;
	_created_at: Date;
};
export type ChatChannel = 'global' | 'game-master';

type Store = {
	skin: GameSkin;
	eventId: string | undefined;
	eventStatus: EventStatus;
	playState: 'intro' | 'registering-members' | 'showing-rat' | 'playing';
	playerLocation: { lat: number; lng: number; heading: number | null; isTracking: boolean; timestamp: number };
	members: { name: string; id: string }[];
	ratId: string | undefined;

	game: Game | undefined;
	teamId: string | undefined;
	teams: TeamRecord[];
	chatMessagesReadCount: {
		global: number;
		'game-master': number;
	};
	chatMessages: {
		global: ChatMessage[];
		'game-master': ChatMessage[];
	};
	activeTaskTab: TaskStatus;
	activeChatTab: ChatChannel;
	roundStates: Record<string, RoundState>;
	taskStates: Record<string, TaskStateLocal>;
	isReadonly: boolean;
	isFrozen: boolean;
	deviceId: string | undefined;
	devices: string[];
};

let lastLocationUpdate = Date.now();
const throttledSendPlayerLocation = (
	eventId: string,
	teamId: string,
	location: { lat: number; lng: number; heading: number | null; isTracking: boolean; timestamp: number },
) => {
	if (Date.now() - lastLocationUpdate < 10_000) {
		return;
	}
	lastLocationUpdate = location.timestamp; //Date.now();
	emitMessage({
		kind: 'event-update-team-location',
		eventId,
		teamId,
		location,
	});
};

function actions(set: (partial: Partial<Store> | ((state: Store) => Partial<Store>)) => void) {
	return {
		init: (init: ClientInit) => {
			queueMicrotask(() => {
				console.log('update gps after init...');
				updateGpsRequired();
			});
			// console.trace('init', init);
			return set({
				eventId: init.eventId,
				eventStatus: init.eventStatus,
				game: init.game.game,
				roundStates: init.roundStates,
				taskStates: init.taskStates,
				teamId: init.teamId,
				members: [],
				ratId: undefined,
				teams: init.teams,
				chatMessages: {
					global: init.chatMessages.global.map((m) => ({ ...m, channel: 'global' })),
					'game-master': init.chatMessages['game-master'].map((m) => ({ ...m, channel: 'game-master' })),
				},
				skin: init.skin ?? appConfig.skin,
				activeChatTab: init.game.game.globalChat ? 'global' : 'game-master',
				isReadonly: false,
				isFrozen: init.eventFrozen,
				// deviceId: undefined,
				// devices: [],
			});
		},
		setSkin: (skin: GameSkin) => set({ skin }),
		setTeamId: (teamId: string) => set({ teamId }),
		setMembers: (members: { name: string; id: string }[]) => set({ members }),
		setRatId: (ratId: string) => set({ ratId }),
		setHideScore: (hideScore: boolean) => {
			return set((state: Store) => {
				if (!state.game) return state;
				const newGame = { ...state.game, hideScore };
				return { game: newGame };
			});
		},
		leaveEvent: () => {
			queueMicrotask(() => {
				// console.log('update gps after leaveEvent...');
				updateGpsRequired();
			});
			return set({
				// NOTE: we intentionally don't reset the skin
				// so the login page is branded with the skin of the last played event
				eventId: undefined,
				game: undefined,
				teamId: undefined,
				members: [],
				ratId: undefined,
				eventStatus: 'draft',
				playState: 'intro',
				teams: [],
				chatMessagesReadCount: {
					global: 0,
					'game-master': 0,
				},
				chatMessages: {
					global: [],
					'game-master': [],
				},
				activeTaskTab: 'todo',
				activeChatTab: 'global',
				roundStates: {},
				taskStates: {},
				isReadonly: false,
				playerLocation: { lat: 0, lng: 0, heading: null, isTracking: false, timestamp: 0 },
			});
		},

		setPlayState: (playState: 'intro' | 'registering-members' | 'showing-rat' | 'playing') => set({ playState }),
		setEventStatus: (eventStatus: EventStatus) => set({ eventStatus }),
		setChatMessagesReadCount: (channel: ChatChannel, count: number) =>
			set((state: Store) => {
				return { chatMessagesReadCount: { ...state.chatMessagesReadCount, [channel]: count } };
			}),

		// pure function for adding a chat message, used when a new message is received from the server
		addChatMessage: (message: ChatMessage) =>
			set((state: Store) => {
				// only add the message if it's not already in the list
				if (state.chatMessages[message.channel].find((m) => m.id === message.id)) {
					console.log('addChatMessage: message already in list, skipping');
					return {};
				}
				return {
					chatMessages: {
						...state.chatMessages,
						[message.channel]: [...state.chatMessages[message.channel], message],
					},
				};
			}),
		// impure function for submitting a chat message, used when the user submits a message
		submitChatMessage: (message: ChatMessage) => {
			return set((state: Store) => {
				if (state.eventId && state.teamId) {
					emitMessage({
						kind: 'event-chat-message',
						message: {
							...message,
							channelId: message.channel === 'global' ? null : state.teamId,
							attachments: message.attachments || [],
							teamId: state.teamId,
							_created_at: new Date(),
						},
						eventId: state.eventId,
					});
					return {
						chatMessages: {
							...state.chatMessages,
							[message.channel]: [...state.chatMessages[message.channel], { ...message, teamId: state.teamId }],
							_created_at: new Date(),
						},
					};
				}
				console.error('SubmitChatMessage: eventId or teamId is undefined, chat message not submitted');
				return {};
			});
		},
		setActiveTaskTab: (tab: TaskStatus) => {
			console.log('setActiveTaskTab', tab);
			return set({ activeTaskTab: tab });
		},
		setActiveChatTab: (tab: ChatChannel) => {
			console.log('setActiveChatTab', tab);
			return set({ activeChatTab: tab });
		},

		// pure function for updating a task submission, used when a task submission is received from the server
		updateTaskSubmission: (submission: TaskState) => {
			return set((state: Store) => {
				console.log('updateTaskSubmission', submission);
				return { taskStates: { ...state.taskStates, [submission.taskId]: submission } };
			});
		},

		// impure function for submitting a task submission, used when the user submits a task
		submitTaskSubmission: (submission: TaskState, file?: File) => {
			console.log('submitTaskSubmission', submission);
			return set((state: Store) => {
				if (state.isReadonly || state.isFrozen) return state;
				const task = state.game?.rounds.flatMap((r) => r.tasks).find((t) => t.id === submission.taskId);
				if (!task) {
					console.error('SubmitTaskSubmission: task not found');
					return state;
				}

				if (state.eventId && state.teamId) {
					uploadTaskSubmission(
						{
							eventId: state.eventId,
							teamId: state.teamId,
							submission: { ...submission },
						},
						file,
					);

					// first set local to true, if all is well the server will call back to updateTaskSubmission to set it to false
					// otherwise it will stay local and we can show a message to the user that the task was not submitted
					return { taskStates: { ...state.taskStates, [submission.taskId]: { ...submission, isLocal: true } } };
				}
				console.error('SubmitTaskSubmission: eventId or teamId is undefined, task submission not submitted');
				return {};
			});
		},

		updateTeamScore: (teamId: string, score: number) => {
			return set((state: Store) => {
				return {
					teams: state.teams.map((team) => (team.id === teamId ? { ...team, score } : team)),
				};
			});
		},

		updateTeamAvatar: (teamId: string, avatar: FileUpload | null) => {
			return set((state: Store) => {
				return {
					teams: state.teams.map((team) =>
						team.id === teamId ? { ...team, data: TeamDataSchema.parse({ ...(team.data ?? {}), avatar }) } : team,
					),
				};
			});
		},

		unlockRounds: (roundIds: string[]) => {
			return set((state: Store) => {
				const newRoundStates: Record<string, RoundState> = { ...state.roundStates };
				for (const roundId of roundIds) {
					if (newRoundStates[roundId]) {
						newRoundStates[roundId] = { ...newRoundStates[roundId], unlocked: true };
					}
				}
				return { roundStates: newRoundStates };
			});
		},

		lockRounds: (roundIds: string[]) => {
			return set((state: Store) => {
				const newRoundStates: Record<string, RoundState> = { ...state.roundStates };
				for (const roundId of roundIds) {
					if (newRoundStates[roundId]) {
						newRoundStates[roundId] = { ...newRoundStates[roundId], unlocked: false };
					}
				}
				return { roundStates: newRoundStates };
			});
		},

		unlockTasks: (taskIds: string[]) => {
			return set((state: Store) => {
				const newTaskStates: Record<string, TaskState> = { ...state.taskStates };
				for (const taskId of taskIds) {
					if (newTaskStates[taskId]) {
						newTaskStates[taskId] = { ...newTaskStates[taskId], unlocked: true };
					}
				}
				return { taskStates: newTaskStates };
			});
		},

		lockTasks: (taskIds: string[]) => {
			return set((state: Store) => {
				const newTaskStates: Record<string, TaskState> = { ...state.taskStates };
				for (const taskId of taskIds) {
					if (newTaskStates[taskId]) {
						newTaskStates[taskId] = { ...newTaskStates[taskId], unlocked: false };
					}
				}
				return { taskStates: newTaskStates };
			});
		},

		updatePlayerLocation: (location: { lat: number; lng: number; heading: number | null; isTracking: boolean; timestamp: number }) => {
			return set((state: Store) => {
				if (state.isReadonly) {
					return { playerLocation: location, roundStates: state.roundStates, taskStates: state.taskStates };
				}
				if (state.eventId && state.teamId) {
					throttledSendPlayerLocation(state.eventId, state.teamId, location);
				}

				const game = state.game;
				const tasks = game?.rounds.flatMap((r) => r.tasks);

				let roundStates = state.roundStates;

				// TODO: rewrite this so we just send the server the roundIds and taskIds to unlock
				// and the server sends us the new roundStates and taskStates

				const roundIdsToUnlock =
					game?.rounds
						.filter((r) => {
							// only unlock gps rounds
							if (r.availability !== 'gps') return false;
							// only unlock if not already unlocked
							if (roundStates[r.id]?.unlocked) return false;
							// Force unlock when game or variant has no lng/lat
							if (!r.availabilityGpsConfig || !r.availabilityGpsConfig.lat || !r.availabilityGpsConfig.lng) return false;
							// only unlock if in range
							const distance = haversineDistance(r.availabilityGpsConfig, location);
							return distance <= r.availabilityGpsConfig.radius;
						})
						.map((r) => r.id) ?? [];

				if (roundIdsToUnlock.length > 0) {
					const newRoundStates: Record<string, RoundState> = {};
					for (const roundState of Object.values(roundStates)) {
						if (roundIdsToUnlock.includes(roundState.roundId)) {
							newRoundStates[roundState.roundId] = { ...roundState, unlocked: true };
						} else {
							newRoundStates[roundState.roundId] = roundState;
						}
					}
					roundStates = newRoundStates;

					emitMessage({
						kind: 'event-change-round-unlock',
						eventId: state.eventId!,
						teamId: state.teamId!,
						roundIds: roundIdsToUnlock,
						unlocked: true,
					});
				}

				// when the player moves, we check if any tasks are now in range and unlock them
				// NOTE: this logic is handled client side, we could move it to the server
				// but the client could cheat either way by spoofing GPS coordinates, so this just saves network traffic
				const taskIdsToUnlock =
					tasks
						?.filter((t) => {
							// only unlock by gps if gps is the unlock condition
							if (t.availability !== 'gps') return false;
							// only unlock if not already unlocked
							if (state.taskStates[t.id]?.unlocked) return false;
							// Force unlock when game or variant has no lng/lat
							if (!t.availabilityGpsConfig || !t.availabilityGpsConfig.lat || !t.availabilityGpsConfig.lng) return false;

							// only unlock if in range
							const distance = haversineDistance(t.availabilityGpsConfig, location);
							const withinRange = distance <= t.availabilityGpsConfig.radius;
							return withinRange;
						})
						.map((t) => t.id) ?? [];

				let taskStates: Record<string, TaskState> | undefined = state.taskStates;

				if (taskIdsToUnlock.length > 0) {
					const newTaskStates: Record<string, TaskState> = {};
					for (const taskState of Object.values(state.taskStates)) {
						if (taskIdsToUnlock.includes(taskState.taskId)) {
							newTaskStates[taskState.taskId] = { ...taskState, unlocked: true };
						} else {
							newTaskStates[taskState.taskId] = taskState;
						}
					}
					taskStates = newTaskStates;

					emitMessage({
						kind: 'event-change-task-unlock',
						eventId: state.eventId!,
						teamId: state.teamId!,
						taskIds: taskIdsToUnlock,
						unlocked: true,
					});
				}
				if (roundIdsToUnlock.length > 0 || taskIdsToUnlock.length > 0) {
					haptics('medium');
					const isOnline = navigator.onLine;

					if (window.location.pathname == '/map' && isOnline) {
						if (roundIdsToUnlock.length > 0 && taskIdsToUnlock.length == 0) {
							state.activeTaskTab = 'todo';
							window.location.pathname = `/rounds/${roundIdsToUnlock[0]}`;
						} else if (taskIdsToUnlock.length > 0) {
							const taskId = taskIdsToUnlock[0];
							const round = state.game?.rounds.find((r) => r.tasks.find((t) => t.id == taskId));
							if (round) {
								state.activeTaskTab = 'todo';
								window.location.pathname = `/rounds/${round.id}/task/${taskId}`;
							}
						}
					}
				}

				return { playerLocation: location, roundStates, taskStates };
			});
		},
		addTeam: (teamId: string, name: string, score: number, rejoinCode: string) => {
			return set((state: Store) => {
				return {
					teams: [...state.teams, { id: teamId, name, rejoinCode, score, data: TeamDataSchema.parse({}), captainId: '' }],
				};
			});
		},
		setReadonlyState: (readonlyState: boolean) => {
			return set(() => {
				return { isReadonly: readonlyState };
			});
		},
		setFreezeState: (freezeState: boolean) => {
			return set(() => {
				return { isFrozen: freezeState };
			});
		},
		setDeviceId: (deviceId: string) => {
			return set(() => ({ deviceId }));
		},
		addDevice: (deviceId: string) => {
			return set((prev) => ({ devices: [...prev.devices, deviceId] }));
		},
	};
}

export const useStore = create<Store & ReturnType<typeof actions>>()(
	persist(
		(set) => ({
			skin: appConfig.skin,
			eventId: undefined,
			eventStatus: 'draft',
			game: undefined,
			teamId: undefined,
			playState: 'intro',
			playerLocation: {
				lat: 0,
				lng: 0,
				heading: null,
				// NOTE: we are optimistic and assume tracking permission will be granted
				// if permission is denied isTracking will be set to false
				isTracking: true,
				timestamp: 0,
			},
			members: [],
			ratId: undefined,
			teams: [],
			chatMessages: {
				global: [],
				'game-master': [],
			},
			chatMessagesReadCount: {
				global: 0,
				'game-master': 0,
			},
			activeTaskTab: 'todo',
			activeChatTab: 'global',
			roundStates: {},
			taskStates: {},
			isReadonly: false,
			isFrozen: false,
			deviceId: undefined,
			devices: [],
			...actions(set),
		}),
		{
			name: 'ubgames-game-store',
		},
	),
);

export const useTeam = () => {
	const teamId = useStore((state) => state.teamId);
	const teams = useStore((state) => state.teams);
	let team = teams.find((team) => team.id === teamId);
	const members = useStore((state) => state.members);
	const position = useStore((state) => state.playerLocation);
	const ratId = useStore((state) => state.ratId);

	if (team) {
		// HACK: patch team with our local state
		team = {
			...team,
			data: {
				...team.data,
				members,
				lat: position.lat,
				lng: position.lng,
				heading: position.heading,
				ratId,
				bonusPoints: 0,
			},
		};
	}

	return team;
};

export const useReadonly = () => {
	const isReadonly = useStore((s) => s.isReadonly);

	return isReadonly;
};

export const useTasks = (roundId: string, state?: TaskStatus) => {
	const tasks = useStore((state) => state.game?.rounds.find((r) => r.id === roundId)?.tasks);
	const taskStates = useStore((state) => state.taskStates);

	return tasks
		?.map((t) => {
			const state = taskStates[t.id] ?? { status: 'todo', taskId: t.id, pointsGiven: 0 };
			return {
				...state,
				...t,
			};
		})
		.filter((t) => (state ? t.status === state : true));
};

export const useCurrentRound = () => {
	const game = useStore((state) => state.game);
	const roundStates = useStore((state) => state.roundStates);
	const taskStates = useStore((state) => state.taskStates);

	const rounds = game?.rounds.map((r) => ({
		...roundStates[r.id],
		...r,
	}));

	const currentRound = rounds?.find((r) => {
		// cannot be current round if not unlocked
		if (!r.unlocked) return false;
		// cannot be current round if all tasks are already completed
		const allTasksCompleted = r.tasks.every((t) => taskStates[t.id]?.status === 'done');
		if (allTasksCompleted) return false;

		return true;
	});

	return currentRound ?? null;
};

export const useCurrentTask = () => {
	const game = useStore((state) => state.game);
	const taskStates = useStore((state) => state.taskStates);

	const rounds = game?.rounds;
	const tasks = rounds
		?.flatMap((r) => r.tasks.map((task) => ({ task, roundTitle: r.title, roundId: r.id })))
		.map((obj) => ({
			task: obj.task,
			taskState: taskStates[obj.task.id],
			roundTitle: obj.roundTitle,
			roundId: obj.roundId,
		}));

	const currentTask = tasks?.find((t) => t.taskState?.status !== 'done' && t.taskState?.unlocked);

	return currentTask ?? { taskState: null, task: null, roundTitle: null, roundId: null };
};

export const useTask = (roundId: string, taskId: string) => {
	const taskStates = useStore((state) => state.taskStates);
	const task = useStore((state) => state.game?.rounds.find((r) => r.id === roundId)?.tasks.find((t) => t.id === taskId));
	if (!task) {
		return { state: null, task: null };
	}
	const state = taskStates[task.id] ?? { status: 'todo', taskId: task.id, pointsGiven: 0 };
	return {
		state,
		task,
	};
};

export type TaskMarker = {
	taskId: string;
	task: GameTask;
	lat: number;
	lng: number;
	radius: number;
	displayName?: string;
	unlocked: boolean;
};

export type TaskOrRoundMarker = TaskMarker | (Omit<TaskMarker, 'taskId' | 'task'> & { roundId: string; round: GameRound });

export const useRoundMarkers = () => {
	const game = useStore((state) => state.game);
	const rounds = game?.rounds;
	const roundStates = useStore((state) => state.roundStates);

	const roundMarkers = rounds
		?.filter((r) => r.availability === 'gps')
		.map((r) => ({
			roundId: r.id,
			round: r,
			lat: r.availabilityGpsConfig?.lat ?? 0,
			lng: r.availabilityGpsConfig?.lng ?? 0,
			radius: r.availabilityGpsConfig?.radius ?? 40,
			unlocked: roundStates[r.id]?.unlocked ?? false,
			showCoordinates: r.availabilityGpsConfig?.showCoordinates ?? true,
		}));

	return roundMarkers ?? [];
};

export const useTaskMarkers = () => {
	const game = useStore((state) => state.game);
	const tasks = game?.rounds.flatMap((r) => r.tasks);
	const taskStates = useStore((state) => state.taskStates);

	const taskMarkers =
		tasks
			?.filter((t) => t.availability === 'gps')
			.map((t) => ({
				taskId: t.id,
				task: t,
				lat: t.availabilityGpsConfig?.lat ?? 0,
				lng: t.availabilityGpsConfig?.lng ?? 0,
				radius: t.availabilityGpsConfig?.radius ?? 40,
				unlocked: taskStates[t.id]?.unlocked ?? false,
				showCoordinates: t.availabilityGpsConfig?.showCoordinates ?? true,
			})) ?? [];

	return taskMarkers;
};

export const useMapMarkers = () => {
	const taskMarkers = useTaskMarkers();
	const roundMarkers = useRoundMarkers();
	return [...taskMarkers, ...roundMarkers];
};

export const useGpsLocation = () => {
	const playerLocation = useStore((state) => state.playerLocation);
	return playerLocation;
};

export const useIsRoundCompleted = (roundId: string) => {
	const tasks = useTasks(roundId);

	return tasks?.filter((t) => t.unlocked).every((t) => ['done', 'in-progress'].includes(t.status));
};

export const useUnreadChatMessageCount = () => {
	const chatMessages = useStore((state) => state.chatMessages);
	const chatMessagesReadCount = useStore((state) => state.chatMessagesReadCount);

	const unreadCountGameMaster = chatMessages['game-master']?.length - chatMessagesReadCount['game-master'];
	const unreadCountGlobal = chatMessages.global?.length - chatMessagesReadCount.global;
	const unreadCount = unreadCountGameMaster + unreadCountGlobal;

	return { unreadCountGameMaster, unreadCountGlobal, unreadCount };
};
